import { useId, type ReactElement } from 'react';
import { Link, type To } from 'react-router';
import type { Channel, ConversationItem } from '@chatapp-inbox/shared';
import {
  BACK_TO_LIST_TARGET,
  DETAILS_LINK_TARGET,
  FOCUS_TARGET_ATTRIBUTE,
} from '../conversation/focus-targets';
import { Avatar, displayName, lineNameWithPhone } from '../conversations';
import styles from './ChatHeader.module.css';

const CHANNEL_NAMES: Record<Channel, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

export interface ChatHeaderProps {
  conversation: ConversationItem;
  /** Rota da lista (o link "Voltar" só aparece em layout estreito). */
  listHref: To;
  /** Para onde o nome do cliente leva: aos detalhes (fechados) ou de volta só ao chat (abertos). */
  detailsHref: To;
  /** Os detalhes estão abertos (o nome do cliente e o botão "Detalhes" os fecham). */
  detailsOpen?: boolean;
}

/**
 * Cabeçalho da conversa: contato, canal, número em que ela acontece, situação, responsável e os
 * atalhos das telas estreitas.
 */
export function ChatHeader({
  conversation,
  listHref,
  detailsHref,
  detailsOpen = false,
}: ChatHeaderProps): ReactElement {
  const { contact } = conversation;
  const hintId = useId();
  const name = displayName(contact);
  const phone = contact.phone?.trim() ?? '';
  // Contato sem nome já aparece pelo telefone no título: repeti-lo na linha de baixo seria ruído.
  const subtitle = [phone && phone !== name ? phone : '', CHANNEL_NAMES[contact.channel]]
    .filter(Boolean)
    .join(' · ');

  return (
    <header className={styles.header}>
      {/* Visível só onde a lista é outra tela (<= 760px); o CSS esconde nas demais larguras. */}
      <Link
        to={listHref}
        className={styles.back}
        {...{ [FOCUS_TARGET_ATTRIBUTE]: BACK_TO_LIST_TARGET }}
      >
        <svg
          className={styles.backIcon}
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M15 5l-7 7 7 7" />
        </svg>
        <span className="sr-only">Voltar para as conversas</span>
      </Link>

      {/* Em telas muito estreitas o círculo sai (`.avatar`): o espaço é do nome, que importa mais. */}
      <span className={styles.avatar}>
        <Avatar contactId={contact.id} name={name} />
      </span>

      <div className={styles.who}>
        <div className={styles.titleRow}>
          <h2 className={styles.name} dir="auto">
            {/* O nome abre (e fecha) o painel de detalhes; é também para onde o foco volta ao fechá-lo. */}
            <Link
              to={detailsHref}
              className={styles.nameLink}
              aria-describedby={hintId}
              {...{ [FOCUS_TARGET_ATTRIBUTE]: DETAILS_LINK_TARGET }}
            >
              {name}
            </Link>
          </h2>
          {/* A dica do link, fora do título (o nome do título continua sendo só o nome do cliente). */}
          <span id={hintId} className="sr-only">
            {detailsOpen ? 'Fechar os detalhes' : 'Abrir os detalhes'}
          </span>
          {conversation.status === 'closed' && <span className={styles.badge}>Encerrada</span>}
        </div>
        <p className={styles.line} dir="auto">
          {subtitle}
        </p>
        {/* Vazia (e escondida pelo CSS) em conversa anterior ao suporte a números: `line` é null. */}
        <p className={styles.line} dir="auto">
          {conversation.line ? `Número: ${lineNameWithPhone(conversation.line)}` : ''}
        </p>
        <p className={styles.line} dir="auto">
          {conversation.assignedTo
            ? `Responsável: ${conversation.assignedTo.name}`
            : 'Sem responsável'}
        </p>
      </div>

      {/* Visível só onde os detalhes são outra tela (<= 1099px): a pessoa nem sempre percebe que o nome é um link. */}
      <Link to={detailsHref} className={styles.details}>
        Detalhes
      </Link>
    </header>
  );
}
